import { useState, useEffect, useCallback } from "react";
import { ShieldCheck, Smartphone, KeyRound, Monitor, History as HistoryIcon, Loader2, Copy, Check, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { timeAgo, clsx } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

export default function Security() {
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [setup, setSetup] = useState(null); // {secret, otpauth_uri}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backup, setBackup] = useState(null);
  const [disableOpen, setDisableOpen] = useState(false);

  const load = useCallback(async () => {
    const [s, se, l] = await Promise.all([
      api.get("/auth/2fa/status"), api.get("/auth/sessions"), api.get("/auth/security-logs"),
    ]);
    setStatus(s.data.enabled); setSessions(se.data); setLogs(l.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const startSetup = async () => {
    const { data } = await api.post("/auth/2fa/setup");
    setSetup(data);
  };
  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      setBackup(data.backup_codes); setSetup(null); setCode("");
      await load(); refreshUser();
      toast.success("2FA enabled");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code });
      setDisableOpen(false); setCode("");
      await load(); refreshUser();
      toast.success("2FA disabled");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const revoke = async (id) => { await api.delete(`/auth/sessions/${id}`); load(); toast.success("Session revoked"); };

  return (
    <div className="p-4 lg:p-8 space-y-6 max-w-3xl">
      <div className="flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-brandblue" /><h1 className="font-display text-3xl font-extrabold tracking-tight">Security</h1></div>

      {/* 2FA */}
      <div className="rounded-xl bg-fx-s1 border border-white/5 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-brandblue/15 grid place-items-center text-brandblue"><Smartphone className="h-5 w-5" /></div>
            <div>
              <div className="font-display font-bold">Two-Factor Authentication</div>
              <div className="text-xs text-fx-s3">Protect your account with an authenticator app.</div>
            </div>
          </div>
          <span data-testid="twofa-status" className={clsx("px-2 py-1 rounded text-xs font-mono", status ? "text-pos bg-pos/15" : "text-fx-s3 bg-white/5")}>{status ? "ENABLED" : "DISABLED"}</span>
        </div>

        {!status && !setup && (
          <Button onClick={startSetup} data-testid="twofa-setup-btn" className="mt-4 bg-brandblue hover:bg-blue-500 gap-2"><KeyRound className="h-4 w-4" /> Enable 2FA</Button>
        )}
        {status && (
          <Button onClick={() => setDisableOpen(true)} data-testid="twofa-disable-btn" variant="outline" className="mt-4 border-white/10 bg-fx-base text-neg">Disable 2FA</Button>
        )}

        {setup && (
          <div className="mt-5 rounded-lg bg-fx-base border border-white/5 p-4 flex flex-col sm:flex-row gap-4 items-center">
            <img alt="qr" className="h-40 w-40 rounded bg-white p-2" src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setup.otpauth_uri)}`} />
            <div className="flex-1 space-y-2 w-full">
              <p className="text-sm text-fx-s3">Scan with Google Authenticator / Authy, or enter the key manually:</p>
              <code className="block text-xs font-mono bg-fx-s2 rounded p-2 break-all">{setup.secret}</code>
              <Label>Enter the 6-digit code</Label>
              <div className="flex gap-2">
                <Input data-testid="twofa-enable-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className="bg-fx-s2 border-white/10 font-mono tracking-widest" />
                <Button onClick={enable} disabled={busy} data-testid="twofa-enable-btn" className="bg-brandblue hover:bg-blue-500">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}</Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sessions */}
      <div className="rounded-xl bg-fx-s1 border border-white/5">
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2"><Monitor className="h-4 w-4 text-brandblue" /><h2 className="font-display font-bold">Login Activity & Devices</h2></div>
        <div className="divide-y divide-white/[0.03] max-h-72 overflow-y-auto">
          {sessions.length === 0 && <div className="px-5 py-8 text-center text-fx-s3 text-sm">No sessions recorded.</div>}
          {sessions.map((s) => (
            <div key={s.id} data-testid={`session-${s.id}`} className="flex items-center justify-between px-5 py-3">
              <div className="min-w-0"><div className="text-sm font-medium truncate">{s.user_agent}</div><div className="text-xs text-fx-s3">{s.ip} · {timeAgo(s.created_at)}</div></div>
              <Button onClick={() => revoke(s.id)} data-testid={`revoke-session-${s.id}`} size="sm" variant="ghost" className="text-neg h-7"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
      </div>

      {/* Security logs */}
      <div className="rounded-xl bg-fx-s1 border border-white/5">
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2"><HistoryIcon className="h-4 w-4 text-brandblue" /><h2 className="font-display font-bold">Security Logs</h2></div>
        <div className="divide-y divide-white/[0.03] max-h-72 overflow-y-auto">
          {logs.length === 0 && <div className="px-5 py-8 text-center text-fx-s3 text-sm">No security events yet.</div>}
          {logs.map((l) => (
            <div key={l.id} className="flex items-center justify-between px-5 py-2.5">
              <span className="text-sm font-mono">{l.event.replace(/_/g, " ")}</span>
              <span className="text-xs text-fx-s3">{l.ip} · {timeAgo(l.created_at)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Backup codes dialog */}
      <Dialog open={!!backup} onOpenChange={(o) => !o && setBackup(null)}>
        <DialogContent className="bg-fx-s2 border-white/10 text-white" data-testid="backup-codes-dialog">
          <DialogHeader><DialogTitle className="font-display">Save your backup codes</DialogTitle></DialogHeader>
          <p className="text-sm text-fx-s3">Store these somewhere safe. Each can be used once if you lose your authenticator.</p>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm my-2">
            {backup?.map((c) => <div key={c} className="bg-fx-base rounded p-2 text-center">{c}</div>)}
          </div>
          <DialogFooter>
            <Button onClick={() => { navigator.clipboard.writeText((backup || []).join("\n")); toast.success("Copied"); }} variant="outline" className="border-white/10 bg-fx-base gap-2"><Copy className="h-4 w-4" /> Copy</Button>
            <Button onClick={() => setBackup(null)} className="bg-brandblue hover:bg-blue-500 gap-2"><Check className="h-4 w-4" /> I've saved them</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable dialog */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent className="bg-fx-s2 border-white/10 text-white" data-testid="disable-2fa-dialog">
          <DialogHeader><DialogTitle className="font-display">Disable 2FA</DialogTitle></DialogHeader>
          <p className="text-sm text-fx-s3">Enter a current code from your authenticator to confirm.</p>
          <Input data-testid="twofa-disable-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className="bg-fx-base border-white/10 font-mono tracking-widest" />
          <DialogFooter><Button onClick={disable} disabled={busy} data-testid="twofa-disable-confirm" className="bg-neg hover:bg-red-500">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disable 2FA"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
